import * as React from 'react';
import SuperCritical from 'lib/supercritical';
import * as cx from 'classnames';
import { noDefault } from 'lib/eventWrappers';

import {
    GridColumn,
    RowSorter,
    RowSelector,
    RowSelectionStatus,
    RowExpander,
    RowExpansionMode
} from './model';

/*
 * GridTableHeaderCells and GridTableRowCells --
 * Functions for producing the core header and row cells in a sortable grid.
 */
export function GridTableHeaderCells<TRow>(columns : GridColumn<TRow>[], m : RowSorter<TRow>, additionalClassName? : string) {
    return columns.map(col =>
        <th className={cx('gridheadercell', col.ClassName, additionalClassName)}
            onClick={() => m.OnSortClick(col)}
            key={col.Title}>
            {col.Title}
            <i className={cx('gridheadercell-icon glyphicon', {
                    'glyphicon-chevron-down' : m.SortColumn() === col && !m.SortReversed(),
                    'glyphicon-chevron-up'   : m.SortColumn() === col &&  m.SortReversed()
                })}></i>
        </th>
    );
}

export function GridTableRowCells<TRow>(columns : GridColumn<TRow>[], row : TRow, additionalClassName? : string) {
    return columns.map(({ Title, ClassName, DisplayValue }) =>
        <td className={cx(ClassName, additionalClassName)} key={Title}>{DisplayValue(row)}</td>
    );
}
/* End GridTableHeaderCells and GridTableRowCells */


/*
 * GridDivHeaderCells and GridDivRowCells --
 * Identical to GridTableHeaderCells and GridTableRowCells except they return <div>'s instead
 * <tr>'s and <td>'s.
 * For use in grids that aren't HTML tables (e.g. our grids w/ expandable nested tables work this way).
 */
export function GridDivHeaderCells<TRow>(columns : GridColumn<TRow>[], m : RowSorter<TRow>, additionalClassName? : string) {
    return columns.map(col =>
        <div className={cx('gridheadercell', col.ClassName, additionalClassName)}
            onClick={() => m.OnSortClick(col)}
            key={col.Title}>
            {col.Title}
            <i className={cx('gridheadercell-icon glyphicon', {
                    'glyphicon-chevron-down' : m.SortColumn() === col && !m.SortReversed(),
                    'glyphicon-chevron-up'   : m.SortColumn() === col &&  m.SortReversed()
                })}></i>
        </div>
    );
}

export function GridDivRowCells<TRow>(columns : GridColumn<TRow>[], row : TRow, additionalClassName? : string) {
    return columns.map(({ Title, ClassName, DisplayValue }) =>
        <div className={cx(ClassName, additionalClassName)} key={Title}>{DisplayValue(row)}</div>
    );
}
/* End GridDivHeaderCells and GridDivRowCells */


/* RowSelectorView -- A none/some/all selector for a list of rows */
export const RowSelectorView = SuperCritical(({ model: m, className } : {
    model : RowSelector,
    className? : string
}) =>
    <span className={cx('rowselectorview', className)}>
        All
        {' '}
        <input type="checkbox" className="rowselectorview-checkbox"
            checked={m.RowSelectionStatus() === RowSelectionStatus.All}
            onChange={e => m.RowSelectionStatus(e.currentTarget.checked ? RowSelectionStatus.All : RowSelectionStatus.None)} />
    </span>
);
/* End RowSelectorView */


/* ExpandCollapseLink -- A Collapse All/Expand All link for grids w/ expandable rows */
export const ExpandCollapseLink = SuperCritical(({ model: m, actionWrapper: _actionWrapper } : { model : RowExpander, actionWrapper? : (action : () => void) => () => void }) => {
    const actionWrapper = _actionWrapper || ((action : () => void) => action);
    return (
        <a href="" onClick={noDefault(actionWrapper(() => m.ExpansionMode(m.AllCurrentlyExpanded() ? RowExpansionMode.AutoCollapse : RowExpansionMode.AutoExpand)))}>
            {m.AllCurrentlyExpanded() ? 'Collapse All' : 'Expand All'}
        </a>
    );
});
/* End ExpandCollapseLink */


// WEBPACK FOOTER //
// ./apps/portcall/gridcomponents/view.tsx